<template>
  <SplitLayout
    :showDetail="showDetail"
    :displayMode="displayMode"
    origin="bottom"
    :willSwipe="true"
    ref="layout">
    <CollectionLayout>
      <CMToolbar
        class="mui-toolbar--search"
        slot="toolbar"
        :nav="toolbarNavItem">
        <input
          slot="title"
          placeholder="Search Cookmark"
          ref="query"
          v-model.trim="query"
          @keyup.enter="enterHandler"
          @blur="blurHandler"
          @focus="focusHandler" />
        <CMButton v-bind="searchActionItem"></CMButton>
      </CMToolbar>
      <RecipeListItem
        v-for="recipe in recipes"
        ref="listItems"
        :key="recipe.key"
        :recipe="recipe"
        :isSelected="recipe.key === recipeKey"
        @click.native="clickHandler(recipe.key)" />
      <RecipeCollectionZeroState
        v-if="hasQuery && recipes.length === 0"
        v-bind="emptyState" />
    </CollectionLayout>
    <Detail
      slot="detail"
      :recipeKey="recipeKey"
      :displayMode="displayMode"
      :showPrimaryHandler="showPrimaryHandler"
      :hidePrimaryHandler="hidePrimaryHandler"
      ref="detail"></Detail>
  </SplitLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import { CollectionLayout, SplitLayout } from '@/layouts'
import { Collection, Detail } from '@/components/Recipe'
import RecipeCollectionZeroState from '@/components/Recipe/CollectionZeroState'
import RecipeListItem from '@/components/Recipe/ListItem'
import CMButton from '@/components/CMButton'
import CMToolbar from '@/components/CMToolbar'
import {
  ADD_SEARCH,
  SET_DRAWER_VISIBILITY,
  REGISTER_SHORTCUT,
  DEREGISTER_SHORTCUT
} from '../modules/mutation-types'

import path from 'path'

export default {
  name: 'SearchView',
  components: {
    CMButton,
    CMToolbar,
    Collection,
    Detail,
    RecipeCollectionZeroState,
    RecipeListItem,
    CollectionLayout,
    SplitLayout
  },
  computed: {
    ...mapGetters([
      'getRecipeById',
      'isSizeClassCompact'
    ]),
    emptyState () {
      return {
        title: `No results for "${this.query}"`,
        message: ''
      }
    },
    encodedQuery () {
      return encodeURIComponent(this.query)
    },
    hasToolbarActionItems () {
      return this.toolbarActionItems && this.toolbarActionItems.length > 0
    },
    hasQuery () {
      return !!this.query
    },
    queryPath () {
      return path.resolve('/search', this.encodedQuery)
    },
    recipes () {
      return this.$store.getters.searchedRecipes(this.query)
    },
    searchActionItem () {
      return {
        name: 'Clear',
        icon: 'close',
        classNames: {
          'cm-button--transparent': true
        },
        fn: this.clearSearch
      }
    },
    showDetail () {
      return Boolean(this.recipeKey) && Boolean(this.displayMode)
    },
    toolbarNavItem () {
      let state = this.$store.state
      if (state.device.sizeClass === 'regular') {
        let visibility = state.app.drawer.visibility === 'visible'
          ? 'hidden'
          : 'visible'
        return {
          name: 'Menu',
          icon: 'menu',
          fn: () => { this.$store.commit(SET_DRAWER_VISIBILITY, { visibility }) }
        }
      }
      return {
        name: 'Close',
        icon: 'navigate_before',
        classNames: {
          'cm-button--transparent': true
        },
        fn: this.returnToPreviousPath
      }
    }
  },
  data () {
    return {
      displayMode: 'adjacent',
      enterQuery: undefined,
      from: {
        path: '/'
      },
      inputFocus: false,
      query: ''
    }
  },
  props: {
    initialQuery: {
      default: '',
      type: String
    },
    recipeKey: String
  },
  methods: {
    clearSearch () {
      this.hasQuery ? this.resetQuery() : this.returnToPreviousPath()
    },
    showPrimaryHandler () {
      this.displayMode = null
    },
    hidePrimaryHandler () {
      this.displayMode = 'primaryHidden'
    },
    resetQuery () {
      this.query = ''
      this.$refs.query.focus()
    },
    returnToPreviousPath () {
      this.$router.currentRoute.path !== this.from.path && this.$router.push(this.from.path)
    },
    blurHandler (skipRouterPush) {
      this.inputFocus = false
      if (!this.hasQuery) return
      this.$store.commit(ADD_SEARCH, {
        query: this.query,
        oldQuery: this.enterQuery
      })
      // if (!skipRouterPush) this.$router.push(this.queryPath)
    },
    clickHandler (key) {
      let path = this.$urlHelpers.searchRecipePath(this.encodedQuery, key)
      this.$router.currentRoute.path !== path && this.$router.push(path)
      if (this.isSizeClassCompact) this.hidePrimaryHandler()
    },
    enterHandler () {
      this.$refs.query.blur()
    },
    focusHandler () {
      this.enterQuery = this.query
      this.inputFocus = true
    },
    registerShortcuts () {
      if (this.inputFocus) {
        this.$store.commit(DEREGISTER_SHORTCUT, 'refine-search')
        this.$store.commit(DEREGISTER_SHORTCUT, 'close-search')
        this.$store.commit(REGISTER_SHORTCUT, {
          name: 'clear-search',
          keycode: 27,
          fn: () => {
            this.clearSearch()
            this.$track('search.clear', {
              context: 'search',
              ability: 'clear'
            })
          },
          scope: 'application'
        })
        this.$store.commit(REGISTER_SHORTCUT, {
          name: 'confirm-search',
          keycode: 13,
          fn: () => {
            this.inputFocus = false
            if (this.recipes.length > 0) this.clickHandler(this.recipes[0].key)
            this.$track('search.confirm', {
              context: 'search',
              ability: 'confirm',
              numResults: this.recipes.length
            })
          },
          scope: 'application'
        })
      } else {
        this.$store.commit(DEREGISTER_SHORTCUT, 'clear-search')
        this.$store.commit(REGISTER_SHORTCUT, {
          name: 'close-search',
          keycode: 27,
          fn: this.returnToPreviousPath,
          scope: 'application'
        })
        this.$store.commit(REGISTER_SHORTCUT, {
          name: 'refine-search',
          keycode: 191,
          fn: () => {
            this.$refs.query.focus()
            this.$track('search.refine', {
              context: 'search',
              ability: 'refine',
              query: this.query
            })
          },
          scope: 'application'
        })
      }
    }
  },
  watch: {
    // Updates to the query force window history updates that don't cause
    // state replacement
    //
    query () {
      document.title = `Search - ${this.query} - Cookmark`
      window.history.replaceState({}, null, this.queryPath)
    },
    // initialQuery is provided through the router when the Search
    // route is entered. It is triggered both on page load and when a query
    // is committed.
    //
    initialQuery () {
      this.query = this.initialQuery
      document.title = `Search - ${this.query} - Cookmark`
      this.$track('search.view', {
        context: 'search',
        ability: 'view',
        query: this.query
      })
    },
    inputFocus () {
      this.registerShortcuts()
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.from = from
      next()
    })
  },
  mounted () {
    this.query = this.initialQuery
    this.registerShortcuts()
    if (!this.hasQuery) this.$refs.query.focus()
    this.$track('search.mount', {
      context: 'search',
      ability: 'mount',
      query: this.query
    })
  },
  beforeDestroy () {
    this.$store.commit(DEREGISTER_SHORTCUT, 'clear-search')
    this.$store.commit(DEREGISTER_SHORTCUT, 'close-search')
    this.$store.commit(DEREGISTER_SHORTCUT, 'confirm-search')
    this.$store.commit(DEREGISTER_SHORTCUT, 'refine-search')
  }
}
</script>



// WEBPACK FOOTER //
// src/views/SearchView.vue